"use client";

import React from "react";
import { Box, Container, Typography, Button, Grid, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useColorScheme } from "@mui/material/styles";
import Security from "@mui/icons-material/Security";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Link from "next/link";

const HeroSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #42A6F6 0%, #0D47A1 100%)",
  color: "white",
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(10),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
  },
}));

// Custom styled component that can access the isDarkMode prop
const CustomHeroSection = styled(Box)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    background: isDarkMode
      ? "linear-gradient(135deg, #1a1a1a 0%, #0d1117 50%, #162028 100%)"
      : "linear-gradient(135deg, #42A6F6 0%, #0D47A1 100%)",
    color: "white",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isDarkMode
        ? 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2342A6F6" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        : 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    },
  })
);

const FeatureBadge = styled(Box)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: isDarkMode
      ? "rgba(66, 166, 246, 0.15)"
      : "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(3),
    border: isDarkMode
      ? "1px solid rgba(66, 166, 246, 0.3)"
      : "1px solid rgba(255, 255, 255, 0.2)",
    marginBottom: theme.spacing(1),
  })
);

const Hero: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");

  return (
    <CustomHeroSection isDarkMode={isDarkMode}>
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10, lg: 8 }}>
            <Stack spacing={4} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  mb: 3,
                }}
              >
                A modern operating system for{" "}
                <Box
                  component="span"
                  sx={{
                    background: "#F49045",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  restructuring professionals
                </Box>
              </Typography>

              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  fontWeight: 400,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: "800px",
                }}
              >
                Purpose-built software for receivers, trustees, and insolvency
                experts — streamlining case management, reporting, and
                stakeholder communication.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 4,
                  justifyContent: "center",
                }}
              >
                <FeatureBadge isDarkMode={isDarkMode}>
                  <AutoAwesome fontSize="small" />
                  <Typography variant="body2">Powered by AI</Typography>
                </FeatureBadge>
                <FeatureBadge isDarkMode={isDarkMode}>
                  <Security fontSize="small" />
                  <Typography variant="body2">
                    Legal-grade privacy standards
                  </Typography>
                </FeatureBadge>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                }}
              >
                <Link
                  href="/contactUs"
                  passHref
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      backgroundColor: "white",
                      color: "#42A6F6",
                      borderColor: "white",
                      py: 2,
                      px: 4,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#0D47A1",
                        color: "white",
                        borderColor: "#0D47A1",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      ...(isDarkMode && {
                        borderColor: "#42A6F6",
                        color: "#42A6F6",
                        backgroundColor: "rgba(66, 166, 246, 0.1)",
                        "&:hover": {
                          backgroundColor: "#42A6F6",
                          color: "#1a1a1a",
                          borderColor: "#42A6F6",
                        },
                      }),
                    }}
                  >
                    Book a demo
                  </Button>
                </Link>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </CustomHeroSection>
  );
};

export default Hero;
